// src/pages/Partner/PartnerCohortDetails.jsx

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Activity,
  ArrowLeft,
  Award,
  BarChart3,
  BookOpen,
  CalendarDays,
  Clock3,
  Copy,
  Edit3,
  ListChecks,
  Mail,
  RefreshCw,
  Trash2,
  UserCog,
  UserPlus,
  Users,
} from "lucide-react";

import {
  Link,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";

import EntrepreneurStatusTabs from "../../components/partner/EntrepreneurStatusTabs";

import {
  getPartnerAttentionStatus,
} from "../../lib/partner/partnerAttention";

import EditCohortModal from "../../components/partner/EditCohortModal";
import InviteLearnerModal from "../../components/partner/InviteLearnerModal";
import PartnerCohortCurriculumPanel from "../../components/partner/PartnerCohortCurriculumPanel";

import {
  archivePartnerCohort,
  fetchPartnerCohortDetail,
} from "../../lib/partner/partnerCohorts";

import {
  fetchPartnerCohortLearnerRoster,
  fetchPartnerCohortProgrammeFormat,
  fetchPartnerCohortRosterCount,
  updatePartnerCohortProgrammeFormat,
} from "../../lib/partner/partnerCohortMetrics";

import {
  fetchPartnerCohortProgress,
} from "../../lib/partner/partnerProgress";

import {
  fetchPartnerMemberCohortResponsibilities,
  fetchPartnerTeam,
  setPartnerMemberCohortResponsibilities,
} from "../../lib/partner/partnerTeam";

import {
  fetchPartnerInvitations,
  getPartnerInvitationLink,
  revokePartnerInvitation,
} from "../../lib/partner/partnerInvitations";

import {
  fetchPartnerLearnerFacilitators,
} from "../../lib/partner/partnerFacilitators";


/* -------------------------------------------------------------------------- */
/* Feature flags                                                              */
/* -------------------------------------------------------------------------- */

/*
 * These features remain fully implemented.
 *
 * They are only hidden from the Partner UI for now.
 *
 * Set any of these to true later when we want to
 * bring the functionality back.
 */

const SHOW_ADVANCED_PROGRAMME_SECTIONS =
  false;

const SHOW_COHORT_CURRICULUM =
  false;

const SHOW_DETAILED_LEARNER_PROGRESS_TABLE =
  false;

const SHOW_LEARNERS_PAGE_NAV =
  false;


/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function nullableNumber(value) {
  if (
    value === null ||
    value === undefined ||
    String(value).trim() === ""
  ) {
    return null;
  }

  const parsed =
    Number(value);

  return Number.isFinite(parsed)
    ? parsed
    : null;
}


function numberValue(value) {
  return (
    nullableNumber(value) ??
    0
  );
}


function metricValue(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return "—";
  }

  return Number(
    value
  ).toLocaleString(
    "en-ZA"
  );
}


function percentageValue(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return "—";
  }

  return `${Number(
    value
  ).toFixed(1)}%`;
}


function formatDate(value) {
  if (!value) {
    return "Not set";
  }

  const date =
    new Date(
      `${value}T00:00:00`
    );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return String(value);
  }

  return new Intl.DateTimeFormat(
    "en-ZA",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }
  ).format(date);
}


function formatDateRange(
  startDate,
  endDate
) {
  if (
    !startDate &&
    !endDate
  ) {
    return "Not set";
  }

  if (
    startDate &&
    !endDate
  ) {
    return `From ${formatDate(
      startDate
    )}`;
  }

  if (
    !startDate &&
    endDate
  ) {
    return `Until ${formatDate(
      endDate
    )}`;
  }

  return `${formatDate(
    startDate
  )} → ${formatDate(
    endDate
  )}`;
}


function formatDuration(
  startDate,
  endDate
) {
  if (
    !startDate ||
    !endDate
  ) {
    return "Not set";
  }

  const start =
    new Date(
      `${startDate}T00:00:00`
    );

  const end =
    new Date(
      `${endDate}T00:00:00`
    );

  if (
    Number.isNaN(
      start.getTime()
    ) ||
    Number.isNaN(
      end.getTime()
    ) ||
    end < start
  ) {
    return "Not set";
  }

  const milliseconds =
    end.getTime() -
    start.getTime();

  const days =
    Math.max(
      1,
      Math.round(
        milliseconds /
          86400000
      ) + 1
    );

  if (days >= 60) {
    const months =
      Math.max(
        1,
        Math.round(
          days / 30.44
        )
      );

    return `${months} ${
      months === 1
        ? "month"
        : "months"
    }`;
  }

  if (days >= 14) {
    const weeks =
      Math.max(
        1,
        Math.round(
          days / 7
        )
      );

    return `${weeks} ${
      weeks === 1
        ? "week"
        : "weeks"
    }`;
  }

  return `${days} ${
    days === 1
      ? "day"
      : "days"
  }`;
}


function formatTimestamp(value) {
  if (!value) {
    return "Not available";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return String(value);
  }

  return new Intl.DateTimeFormat(
    "en-ZA",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }
  ).format(date);
}


function formatActivityAge(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return "No activity";
  }

  const days =
    Number(value);

  if (
    !Number.isFinite(days)
  ) {
    return "No activity";
  }

  if (days <= 0) {
    return "Today";
  }

  if (days === 1) {
    return "Yesterday";
  }

  return `${days} days ago`;
}


function formatShortDate(date) {
  return new Intl.DateTimeFormat(
    "en-ZA",
    {
      day: "2-digit",
      month: "short",
    }
  ).format(date);
}


function statusClasses(status) {
  switch (status) {
    case "active":
      return "bg-green-100 text-green-800";

    case "upcoming":
      return "bg-blue-100 text-blue-800";

    case "completed":
      return "bg-blue-100 text-blue-800";

    case "archived":
      return "bg-gray-200 text-gray-700";

    default:
      return "bg-amber-100 text-amber-800";
  }
}


function programmeTypeLabel(value) {
  switch (
    String(value || "")
      .trim()
      .toLowerCase()
  ) {
    case "incubator":
      return "Incubator";

    case "accelerator":
      return "Accelerator";

    case "supplier":
      return "Supplier";

    case "development":
      return "Development";

    case "other":
      return "Other";

    default:
      return "Programme type not set";
  }
}


function getJohannesburgDateKey() {
  const parts =
    new Intl.DateTimeFormat(
      "en-US",
      {
        timeZone:
          "Africa/Johannesburg",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }
    ).formatToParts(
      new Date()
    );

  const value = (type) =>
    parts.find(
      (part) =>
        part.type === type
    )?.value || "";

  return `${value(
    "year"
  )}-${value(
    "month"
  )}-${value(
    "day"
  )}`;
}


function getCohortDisplayStatus(
  cohort
) {
  const storedStatus =
    cohort?.status ||
    "draft";

  if (storedStatus !== "active") {
    return storedStatus;
  }

  const today =
    getJohannesburgDateKey();

  if (
    cohort?.end_date &&
    cohort.end_date < today
  ) {
    return "completed";
  }

  if (
    cohort?.start_date &&
    cohort.start_date > today
  ) {
    return "upcoming";
  }

  return "active";
}


function invitationStatusClasses(
  status
) {
  switch (status) {
    case "accepted":
      return "bg-green-100 text-green-800";

    case "pending":
      return "bg-amber-100 text-amber-800";

    case "expired":
      return "bg-gray-200 text-gray-700";

    case "revoked":
      return "bg-red-100 text-red-700";

    default:
      return "bg-gray-100 text-gray-700";
  }
}


function progressPositionLabel(
  learner
) {
  if (
    learner
      ?.last_completed_level_title
  ) {
    return (
      learner
        .last_completed_level_title
    );
  }

  const parts = [
    learner?.current_quest_id
      ? `Quest ${learner.current_quest_id}`
      : null,

    learner?.current_subquest_id
      ? `Subquest ${learner.current_subquest_id}`
      : null,

    learner?.current_level_key ||
      null,
  ].filter(Boolean);

  if (parts.length === 0) {
    return "Not started";
  }

  return parts.join(" · ");
}


function learnerActivityStatus(
  learner
) {
  const days =
    nullableNumber(
      learner?.days_since_activity
    );

  if (days === null) {
    return {
      label:
        "Not started",

      detail:
        "No activity recorded",
    };
  }

  if (days >= 7) {
    return {
      label:
        "Inactive",

      detail:
        `Last active ${days} days ago`,
    };
  }

  if (days >= 3) {
    return {
      label:
        "Watch",

      detail:
        `Last active ${days} days ago`,
    };
  }

  if (days === 0) {
    return {
      label:
        "Active",

      detail:
        "Active today",
    };
  }

  if (days === 1) {
    return {
      label:
        "Active",

      detail:
        "Last active yesterday",
    };
  }

  return {
    label:
      "Active",

    detail:
      `Last active ${days} days ago`,
  };
}


/* -------------------------------------------------------------------------- */
/* Trend helpers                                                              */
/* -------------------------------------------------------------------------- */

function startOfWeek(value) {
  const date =
    new Date(value);

  date.setHours(
    0,
    0,
    0,
    0
  );

  const currentDay =
    date.getDay();

  const mondayOffset =
    currentDay === 0
      ? -6
      : 1 - currentDay;

  date.setDate(
    date.getDate() +
      mondayOffset
  );

  return date;
}


function addDays(
  value,
  dayCount
) {
  const date =
    new Date(value);

  date.setDate(
    date.getDate() +
      dayCount
  );

  return date;
}


function buildWeeklyTrend(
  rows,
  field,
  weekCount
) {
  const currentWeekStart =
    startOfWeek(
      new Date()
    );

  const firstWeekStart =
    addDays(
      currentWeekStart,
      -7 *
        (weekCount - 1)
    );

  const buckets =
    Array.from(
      {
        length:
          weekCount,
      },
      (_, index) => {
        const start =
          addDays(
            firstWeekStart,
            index * 7
          );

        const end =
          addDays(
            start,
            7
          );

        return {
          key:
            start.toISOString(),

          start,

          end,

          label:
            formatShortDate(
              start
            ),

          count: 0,
        };
      }
    );

  rows.forEach(
    (row) => {
      const rawValue =
        row?.[field];

      if (!rawValue) {
        return;
      }

      const date =
        new Date(
          rawValue
        );

      if (
        Number.isNaN(
          date.getTime()
        )
      ) {
        return;
      }

      const bucket =
        buckets.find(
          (item) =>
            date >=
              item.start &&
            date <
              item.end
        );

      if (bucket) {
        bucket.count += 1;
      }
    }
  );

  return buckets;
}


/* -------------------------------------------------------------------------- */
/* Programme metrics                                                          */
/* -------------------------------------------------------------------------- */

function calculateProgrammeMetrics(
  rows
) {
  if (!Array.isArray(rows)) {
    return null;
  }

  const activated =
    rows.filter(
      (row) =>
        numberValue(
          row.levels_completed
        ) > 0 ||
        numberValue(
          row.tasks_completed
        ) > 0
    ).length;

  const activeThisWeek =
    rows.filter(
      (row) => {
        const days =
          nullableNumber(
            row.days_since_activity
          );

        return (
          days !== null &&
          days >= 0 &&
          days < 7
        );
      }
    ).length;

  const progressValues =
    rows
      .map(
        (row) =>
          nullableNumber(
            row.overall_progress_percent
          )
      )
      .filter(
        (value) =>
          value !== null
      );

  const averageProgress =
    progressValues.length > 0
      ? progressValues.reduce(
          (
            total,
            value
          ) =>
            total +
            value,
          0
        ) /
        progressValues.length
      : 0;

  const levelsCompleted =
    rows.reduce(
      (
        total,
        row
      ) =>
        total +
        numberValue(
          row.levels_completed
        ),
      0
    );

  const tasksCompleted =
    rows.reduce(
      (
        total,
        row
      ) =>
        total +
        numberValue(
          row.tasks_completed
        ),
      0
    );

  const tasksOutstanding =
    rows.reduce(
      (
        total,
        row
      ) =>
        total +
        numberValue(
          row.tasks_outstanding
        ),
      0
    );

  return {
    activated,
    activeThisWeek,
    averageProgress,
    levelsCompleted,
    tasksCompleted,
    tasksOutstanding,
  };
}


/* -------------------------------------------------------------------------- */
/* Attention                                                                  */
/* -------------------------------------------------------------------------- */

function daysSinceTimestamp(
  value
) {
  if (!value) {
    return null;
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return null;
  }

  const difference =
    Date.now() -
    date.getTime();

  return Math.max(
    0,
    Math.floor(
      difference /
        86400000
    )
  );
}


function calculateNeedsAttentionCount(
  rows
) {
  if (!Array.isArray(rows)) {
    return null;
  }

  return rows.filter(
    (learner) => {
      const daysSinceActivity =
        nullableNumber(
          learner.days_since_activity
        );

      /*
       * Watch:
       * 3–6 days without meaningful activity.
       *
       * Inactive:
       * 7+ days without meaningful activity.
       */
      const activityNeedsAttention =
        daysSinceActivity !==
          null &&
        daysSinceActivity >= 3;

      /*
       * Stalled:
       * learner has started but has not
       * progressed for at least 7 days.
       */
      const hasStarted =
        numberValue(
          learner.levels_completed
        ) > 0 ||
        numberValue(
          learner.tasks_completed
        ) > 0 ||
        Boolean(
          learner.current_quest_id ||
            learner.current_subquest_id ||
            learner.current_level_key
        );

      const progressionDates = [
        learner
          .current_position_updated_at,

        learner
          .last_completed_at,

        learner
          .last_task_completed_at,
      ]
        .filter(Boolean)
        .map(
          (value) =>
            new Date(value)
        )
        .filter(
          (date) =>
            !Number.isNaN(
              date.getTime()
            )
        );

      const latestProgressDate =
        progressionDates.length > 0
          ? new Date(
              Math.max(
                ...progressionDates.map(
                  (date) =>
                    date.getTime()
                )
              )
            )
          : null;

      const daysSinceProgress =
        latestProgressDate
          ? daysSinceTimestamp(
              latestProgressDate
            )
          : null;

      const stalled =
        hasStarted &&
        daysSinceProgress !==
          null &&
        daysSinceProgress >= 7;

      return (
        stalled ||
        activityNeedsAttention
      );
    }
  ).length;
}


/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

export default function PartnerCohortDetails() {
  const {
    cohortId,
  } = useParams();

  const navigate =
    useNavigate();

  const {
    access,
  } =
    useOutletContext();


  /* ------------------------------------------------------------------------ */
  /* Role                                                                     */
  /* ------------------------------------------------------------------------ */

  const role =
    access?.partner_role ||
    "";

  const isFacilitator =
    role ===
    "facilitator";

  const canManageTeam =
    [
      "owner",
      "admin",
    ].includes(role);

  const canViewFacilitatorTags =
    [
      "owner",
      "admin",
      "cohort_manager",
    ].includes(role);


  /* ------------------------------------------------------------------------ */
  /* Data                                                                     */
  /* ------------------------------------------------------------------------ */

  const [
    cohort,
    setCohort,
  ] = useState(null);

  const [
    rosterCounts,
    setRosterCounts,
  ] = useState(null);

  /*
   * null = unavailable
   * [] = successfully loaded,
   *      but no learners
   */
  const [
    progressRows,
    setProgressRows,
  ] = useState(null);

  const [
    facilitators,
    setFacilitators,
  ] = useState(null);

  const [
    facilitatorAssignments,
    setFacilitatorAssignments,
  ] = useState([]);

  const [
    teamMembers,
    setTeamMembers,
  ] = useState([]);

  const [
    cohortResponsibilitiesByMemberId,
    setCohortResponsibilitiesByMemberId,
  ] = useState({});

  const [
    invitations,
    setInvitations,
  ] = useState([]);


  /* ------------------------------------------------------------------------ */
  /* Future programme features                                                */
  /* ------------------------------------------------------------------------ */

  const [
    programmeFormat,
    setProgrammeFormat,
  ] = useState("");

  const [
    formatDraft,
    setFormatDraft,
  ] = useState("");

  const [
    editingFormat,
    setEditingFormat,
  ] = useState(false);

  const [
    savingFormat,
    setSavingFormat,
  ] = useState(false);

  const [
    trendWeeks,
    setTrendWeeks,
  ] = useState(6);


  /* ------------------------------------------------------------------------ */
  /* UI state                                                                 */
  /* ------------------------------------------------------------------------ */

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    archiving,
    setArchiving,
  ] = useState(false);

  const [
    editing,
    setEditing,
  ] = useState(false);

  const [
    inviting,
    setInviting,
  ] = useState(false);

  const [
    changingInvitationId,
    setChangingInvitationId,
  ] = useState("");

  const [
    copyingInvitationId,
    setCopyingInvitationId,
  ] = useState("");

  const [
    copiedInvitationId,
    setCopiedInvitationId,
  ] = useState("");

  const [
    savingStaffRole,
    setSavingStaffRole,
  ] = useState("");


  /* ------------------------------------------------------------------------ */
  /* Errors/messages                                                          */
  /* ------------------------------------------------------------------------ */

  const [
    error,
    setError,
  ] = useState("");

  const [
    countsError,
    setCountsError,
  ] = useState("");

  const [
    insightsError,
    setInsightsError,
  ] = useState("");

  const [
    facilitatorError,
    setFacilitatorError,
  ] = useState("");

  const [
    formatError,
    setFormatError,
  ] = useState("");

  const [
    invitationsError,
    setInvitationsError,
  ] = useState("");

  const [
    message,
    setMessage,
  ] = useState("");


  /* ------------------------------------------------------------------------ */
  /* Load cohort                                                              */
  /* ------------------------------------------------------------------------ */

  const loadCohort =
    useCallback(
      async () => {
        try {
          setLoading(true);

          setError("");
          setCountsError("");
          setInsightsError("");
          setFacilitatorError("");
          setFormatError("");
          setInvitationsError("");

          const cohortRow =
            await fetchPartnerCohortDetail(
              cohortId
            );

          if (!cohortRow) {
            throw new Error(
              "The cohort could not be found or you do not have access to it."
            );
          }

          setCohort(
            cohortRow
          );

          const [
            countsResult,
            rosterResult,
            progressResult,
            formatResult,
            teamResult,
            invitationsResult,
            learnerFacilitatorsResult,
          ] =
            await Promise.allSettled(
              [
                fetchPartnerCohortRosterCount(
                  cohortId
                ),

                fetchPartnerCohortLearnerRoster(
                  cohortId
                ),

                fetchPartnerCohortProgress(
                  cohortId
                ),

                fetchPartnerCohortProgrammeFormat(
                  cohortId
                ),

                cohortRow.can_manage
                  ? fetchPartnerTeam(
                      cohortRow.partner_id
                    )
                  : Promise.resolve(
                      []
                    ),

                cohortRow.can_manage
                  ? fetchPartnerInvitations(
                      cohortId
                    )
                  : Promise.resolve(
                      []
                    ),

                canViewFacilitatorTags
                  ? fetchPartnerLearnerFacilitators(
                      cohortId
                    )
                  : Promise.resolve(
                      []
                    ),
              ]
            );


          /* ---------------------------------------------------------------- */
          /* Roster totals                                                    */
          /* ---------------------------------------------------------------- */

          if (
            countsResult.status ===
            "fulfilled"
          ) {
            setRosterCounts(
              countsResult.value
            );
          } else {
            console.error(
              "Cohort roster count loading failed:",
              countsResult.reason
            );

            setRosterCounts(
              null
            );

            setCountsError(
              countsResult.reason
                ?.message ||
                "Learner membership totals could not be loaded."
            );
          }


          /* ---------------------------------------------------------------- */
          /* Safe learner progress                                            */
          /* ---------------------------------------------------------------- */

          if (
            rosterResult.status ===
              "fulfilled" &&
            progressResult.status ===
              "fulfilled"
          ) {
            const safeUserIds =
              new Set(
                (
                  Array.isArray(
                    rosterResult.value
                  )
                    ? rosterResult.value
                    : []
                )
                  .map(
                    (row) =>
                      row.user_id
                  )
                  .filter(Boolean)
                  .map(String)
              );

            const safeProgressRows =
              (
                Array.isArray(
                  progressResult.value
                )
                  ? progressResult.value
                  : []
              ).filter(
                (row) =>
                  row.user_id &&
                  safeUserIds.has(
                    String(
                      row.user_id
                    )
                  )
              );

            setProgressRows(
              safeProgressRows
            );
          } else {
            const reason =
              rosterResult.status ===
              "rejected"
                ? rosterResult.reason
                : progressResult.reason;

            console.error(
              "Cohort programme insight loading failed:",
              reason
            );

            /*
             * Never use unfiltered progress
             * when the safe roster fails.
             */
            setProgressRows(
              null
            );

            setInsightsError(
              reason?.message ||
                "Programme insight data could not be loaded safely."
            );
          }


          /* ---------------------------------------------------------------- */
          /* Programme format                                                 */
          /* ---------------------------------------------------------------- */

          if (
            formatResult.status ===
            "fulfilled"
          ) {
            const nextFormat =
              String(
                formatResult.value ||
                  ""
              ).trim();

            setProgrammeFormat(
              nextFormat
            );

            setFormatDraft(
              nextFormat
            );
          } else {
            console.error(
              "Programme format loading failed:",
              formatResult.reason
            );

            setProgrammeFormat(
              ""
            );

            setFormatDraft(
              ""
            );

            setFormatError(
              formatResult.reason
                ?.message ||
                "Programme format could not be loaded."
            );
          }


          /* ---------------------------------------------------------------- */
          /* Staff                                                            */
          /* ---------------------------------------------------------------- */

          if (
            teamResult.status ===
            "fulfilled"
          ) {
            const teamRows =
              Array.isArray(
                teamResult.value
              )
                ? teamResult.value
                : [];

            setTeamMembers(
              teamRows
            );

            const responsibilityResults =
              await Promise.allSettled(
                teamRows
                  .filter(
                    (member) =>
                      member.status ===
                      "active"
                  )
                  .map(
                    async (member) => {
                      const rows =
                        await fetchPartnerMemberCohortResponsibilities(
                          member.partner_member_id
                        );

                      const row =
                        rows.find(
                          (item) =>
                            String(
                              item.cohort_id
                            ) ===
                            String(
                              cohortId
                            )
                        );

                      return [
                        String(
                          member.partner_member_id
                        ),
                        {
                          isProgrammeManager:
                            Boolean(
                              row?.is_programme_manager
                            ),
                          isFacilitator:
                            Boolean(
                              row?.is_facilitator
                            ),
                        },
                      ];
                    }
                  )
              );

            const responsibilityEntries =
              responsibilityResults
                .filter(
                  (result) =>
                    result.status ===
                    "fulfilled"
                )
                .map(
                  (result) =>
                    result.value
                );

            const responsibilityMap =
              Object.fromEntries(
                responsibilityEntries
              );

            setCohortResponsibilitiesByMemberId(
              responsibilityMap
            );

            setFacilitators(
              teamRows.filter(
                (member) =>
                  member.status ===
                    "active" &&
                  Boolean(
                    responsibilityMap[
                      String(
                        member.partner_member_id
                      )
                    ]?.isFacilitator
                  )
              )
            );
          } else {
            console.error(
              "Cohort staff loading failed:",
              teamResult.reason
            );

            setTeamMembers(
              []
            );

            setCohortResponsibilitiesByMemberId(
              {}
            );

            setFacilitators(
              null
            );

            setFacilitatorError(
              teamResult.reason
                ?.message ||
                "Cohort staff information could not be loaded."
            );
          }


          /* ---------------------------------------------------------------- */
          /* Invitations                                                      */
          /* ---------------------------------------------------------------- */

          if (
            invitationsResult.status ===
            "fulfilled"
          ) {
            setInvitations(
              Array.isArray(
                invitationsResult.value
              )
                ? invitationsResult.value
                : []
            );
          } else {
            console.error(
              "Cohort invitation loading failed:",
              invitationsResult.reason
            );

            setInvitations(
              []
            );

            setInvitationsError(
              invitationsResult.reason
                ?.message ||
                "Cohort invitations could not be loaded."
            );
          }


          /* ---------------------------------------------------------------- */
          /* Learner facilitator assignments                                  */
          /* ---------------------------------------------------------------- */

          if (
            learnerFacilitatorsResult.status ===
            "fulfilled"
          ) {
            setFacilitatorAssignments(
              Array.isArray(
                learnerFacilitatorsResult.value
              )
                ? learnerFacilitatorsResult.value
                : []
            );
          } else {
            console.error(
              "Learner facilitator assignments loading failed:",
              learnerFacilitatorsResult.reason
            );

            setFacilitatorAssignments(
              []
            );
          }
        } catch (
          loadError
        ) {
          console.error(
            "Cohort detail loading failed:",
            loadError
          );

          setCohort(
            null
          );

          setError(
            loadError?.message ||
              "The cohort could not be loaded."
          );
        } finally {
          setLoading(
            false
          );
        }
      },
      [
        cohortId,
        canViewFacilitatorTags,
      ]
    );


  useEffect(() => {
    loadCohort();
  }, [loadCohort]);


  /* ------------------------------------------------------------------------ */
  /* Programme calculations                                                   */
  /* ------------------------------------------------------------------------ */

  const programmeMetrics =
    useMemo(
      () =>
        calculateProgrammeMetrics(
          progressRows
        ),
      [progressRows]
    );

  const needsAttentionCount =
    useMemo(
      () =>
        calculateNeedsAttentionCount(
          progressRows
        ),
      [progressRows]
    );

  const activityTrend =
    useMemo(
      () =>
        progressRows === null
          ? []
          : buildWeeklyTrend(
              progressRows,
              "last_activity_at",
              trendWeeks
            ),
      [
        progressRows,
        trendWeeks,
      ]
    );

  const levelTrend =
    useMemo(
      () =>
        progressRows === null
          ? []
          : buildWeeklyTrend(
              progressRows,
              "last_completed_at",
              trendWeeks
            ),
      [
        progressRows,
        trendWeeks,
      ]
    );

  const taskTrend =
    useMemo(
      () =>
        progressRows === null
          ? []
          : buildWeeklyTrend(
              progressRows,
              "last_task_completed_at",
              trendWeeks
            ),
      [
        progressRows,
        trendWeeks,
      ]
    );


  /* ------------------------------------------------------------------------ */
  /* Archive                                                                  */
  /* ------------------------------------------------------------------------ */

  async function handleArchive() {
    const confirmed =
      window.confirm(
        "Archive this cohort? It will remain available in your records, but its status will change to archived."
      );

    if (!confirmed) {
      return;
    }

    try {
      setArchiving(
        true
      );

      setError("");
      setMessage("");

      await archivePartnerCohort(
        cohortId
      );

      await loadCohort();

      setMessage(
        "The cohort was archived."
      );
    } catch (
      archiveError
    ) {
      console.error(
        "Cohort archive failed:",
        archiveError
      );

      setError(
        archiveError?.message ||
          "The cohort could not be archived."
      );
    } finally {
      setArchiving(
        false
      );
    }
  }


  /* ------------------------------------------------------------------------ */
  /* Invitation created                                                       */
  /* ------------------------------------------------------------------------ */

  async function handleInvited(
    result
  ) {
    setMessage(
      result?.inviteeName
        ? `Invitation sent to ${result.inviteeName}.`
        : `Invitation sent to ${result?.email || "the entrepreneur"}.`
    );

    setCopiedInvitationId("");

    await loadCohort();
  }


  /* ------------------------------------------------------------------------ */
  /* Copy invitation link                                                     */
  /* ------------------------------------------------------------------------ */

  async function handleCopyInvitationLink(
    invitation
  ) {
    const invitationId =
      String(
        invitation?.invitation_id ||
          ""
      ).trim();

    if (!invitationId) {
      return;
    }

    try {
      setCopyingInvitationId(
        invitationId
      );

      setError("");

      const inviteUrl =
        await getPartnerInvitationLink(
          invitationId
        );

      if (!inviteUrl) {
        throw new Error(
          "This invitation link is not available. If this invitation was created before invitation-link storage was enabled, resend the invitation once to generate a fresh link."
        );
      }

      await navigator.clipboard.writeText(
        inviteUrl
      );

      setCopiedInvitationId(
        invitationId
      );

      window.setTimeout(
        () => {
          setCopiedInvitationId(
            (current) =>
              current ===
              invitationId
                ? ""
                : current
          );
        },
        2000
      );
    } catch (copyError) {
      console.error(
        "Copy invitation link failed:",
        copyError
      );

      setError(
        copyError?.message ||
          "The invitation link could not be copied."
      );
    } finally {
      setCopyingInvitationId("");
    }
  }


  /* ------------------------------------------------------------------------ */
  /* Scroll to visible learner progress table                                 */
  /* ------------------------------------------------------------------------ */

  function handleOpenLearnerProgress() {
    document
      .getElementById(
        "learner-progress"
      )
      ?.scrollIntoView({
        behavior:
          "smooth",

        block:
          "start",
      });
  }


  /* ------------------------------------------------------------------------ */
  /* Revoke invitation                                                        */
  /* ------------------------------------------------------------------------ */

  async function handleRevokeInvitation(
    invitation
  ) {
    const invitationName =
      invitation.invitee_name ||
      invitation.email ||
      "this entrepreneur";

    const confirmed =
      window.confirm(
        `Revoke the invitation for ${invitationName}? The current invitation will no longer be usable.`
      );

    if (!confirmed) {
      return;
    }

    try {
      setChangingInvitationId(
        invitation.invitation_id
      );

      setError("");
      setMessage("");

      await revokePartnerInvitation(
        invitation.invitation_id
      );

      setMessage(
        `The invitation for ${invitation.email} was revoked.`
      );

      await loadCohort();
    } catch (
      revokeError
    ) {
      console.error(
        "Revoking cohort invitation failed:",
        revokeError
      );

      setError(
        revokeError?.message ||
          "The invitation could not be revoked."
      );
    } finally {
      setChangingInvitationId(
        ""
      );
    }
  }


  /* ------------------------------------------------------------------------ */
  /* Cohort staff responsibility dropdowns                                   */
  /* ------------------------------------------------------------------------ */

  async function handleStaffRoleChange(
    field,
    partnerMemberId
  ) {
    if (!canManageTeam || !cohort?.id) {
      return;
    }

    const selectedId =
      String(
        partnerMemberId ||
          ""
      );

    const affectedMembers =
      teamMembers.filter(
        (member) => {
          if (
            member.status !==
            "active"
          ) {
            return false;
          }

          const memberId =
            String(
              member.partner_member_id
            );

          const current =
            cohortResponsibilitiesByMemberId[
              memberId
            ] || {
              isProgrammeManager:
                false,
              isFacilitator:
                false,
            };

          const currentlySelected =
            field ===
            "isProgrammeManager"
              ? current.isProgrammeManager
              : current.isFacilitator;

          return (
            currentlySelected ||
            memberId === selectedId
          );
        }
      );

    try {
      setSavingStaffRole(field);
      setError("");
      setMessage("");

      await Promise.all(
        affectedMembers.map(
          (member) => {
            const memberId =
              String(
                member.partner_member_id
              );

            const current =
              cohortResponsibilitiesByMemberId[
                memberId
              ] || {
                isProgrammeManager:
                  false,
                isFacilitator:
                  false,
              };

            return setPartnerMemberCohortResponsibilities({
              partnerMemberId:
                member.partner_member_id,
              cohortId:
                cohort.id,
              isProgrammeManager:
                field ===
                "isProgrammeManager"
                  ? memberId === selectedId
                  : current.isProgrammeManager,
              isFacilitator:
                field ===
                "isFacilitator"
                  ? memberId === selectedId
                  : current.isFacilitator,
            });
          }
        )
      );

      setMessage(
        field ===
          "isProgrammeManager"
          ? "Programme Manager updated."
          : "Facilitator updated."
      );

      await loadCohort();
    } catch (staffError) {
      console.error(
        "Updating cohort staff responsibility failed:",
        staffError
      );

      setError(
        staffError?.message ||
          "The cohort responsibility could not be updated."
      );
    } finally {
      setSavingStaffRole("");
    }
  }


  /* ------------------------------------------------------------------------ */
  /* Future programme format                                                  */
  /* ------------------------------------------------------------------------ */

  async function handleSaveFormat() {
    try {
      setSavingFormat(
        true
      );

      setFormatError("");
      setMessage("");

      const savedFormat =
        await updatePartnerCohortProgrammeFormat(
          cohortId,
          formatDraft
        );

      setProgrammeFormat(
        savedFormat
      );

      setFormatDraft(
        savedFormat
      );

      setEditingFormat(
        false
      );

      setMessage(
        "Programme format updated."
      );
    } catch (
      saveError
    ) {
      console.error(
        "Programme format update failed:",
        saveError
      );

      setFormatError(
        saveError?.message ||
          "Programme format could not be updated."
      );
    } finally {
      setSavingFormat(
        false
      );
    }
  }


  /* ------------------------------------------------------------------------ */
  /* Loading                                                                  */
  /* ------------------------------------------------------------------------ */

  if (loading) {
    return (
      <div className="rounded-[24px] border border-[#EFEFEF] bg-white p-8 text-center text-sm font-semibold text-[#757575]">
        <RefreshCw className="mx-auto h-6 w-6 animate-spin text-[#FF914D]" />

        <p className="mt-3">
          Loading cohort…
        </p>
      </div>
    );
  }


  /* ------------------------------------------------------------------------ */
  /* Missing cohort                                                           */
  /* ------------------------------------------------------------------------ */

  if (
    error &&
    !cohort
  ) {
    return (
      <section className="rounded-[28px] border border-red-200 bg-white p-6 shadow-sm">

        <h1 className="text-xl font-black text-[#333333]">
          Cohort unavailable
        </h1>

        <p className="mt-3 text-sm text-red-700">
          {error}
        </p>

        <div className="mt-6 flex flex-wrap gap-3">

          <button
            type="button"
            onClick={
              loadCohort
            }
            className="inline-flex items-center rounded-full bg-[#FF914D] px-5 py-3 text-sm font-black text-white transition hover:bg-[#E9782F]"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </button>

          <button
            type="button"
            onClick={() =>
              navigate(
                "/partner/cohorts"
              )
            }
            className="rounded-full border border-[#DDDDDD] px-5 py-3 text-sm font-black text-[#333333] transition hover:border-[#FF914D] hover:text-[#FF914D]"
          >
            Back to cohorts
          </button>

        </div>

      </section>
    );
  }


  const cohortLifecycleStatus =
    getCohortDisplayStatus(
      cohort
    );

  const mayInvite =
    cohort.can_manage &&
    [
      "active",
      "upcoming",
    ].includes(
      cohortLifecycleStatus
    );

  const registeredLearners =
    rosterCounts
      ? numberValue(
          rosterCounts.learner_count
        )
      : null;

  const activeLearners =
    rosterCounts
      ? numberValue(
          rosterCounts.active_learner_count
        )
      : null;

  const completedLearners =
    rosterCounts
      ? numberValue(
          rosterCounts.completed_learner_count
        )
      : null;

  const pendingInvitations =
    isFacilitator
      ? null
      : numberValue(
          cohort.pending_invitation_count
        );

  const learnerPipeline =
    registeredLearners ===
    null
      ? null
      : isFacilitator
      ? registeredLearners
      : registeredLearners +
        numberValue(
          pendingInvitations
        );


  /* ------------------------------------------------------------------------ */
  /* Staff                                                                    */
  /* ------------------------------------------------------------------------ */

  const assignedTeamMembers =
    teamMembers.filter(
      (member) => {
        if (
          member.status !==
          "active"
        ) {
          return false;
        }

        const responsibility =
          cohortResponsibilitiesByMemberId[
            String(
              member.partner_member_id
            )
          ];

        return Boolean(
          responsibility?.isProgrammeManager ||
          responsibility?.isFacilitator
        );
      }
    );

  const programmeManagers =
    assignedTeamMembers.filter(
      (member) =>
        Boolean(
          cohortResponsibilitiesByMemberId[
            String(
              member.partner_member_id
            )
          ]?.isProgrammeManager
        )
    );

  const programmeManagerNames =
    programmeManagers.length ===
    0
      ? facilitatorError
        ? "Not available"
        : "Unassigned"
      : programmeManagers
          .map(
            (member) =>
              member.full_name ||
              member.email ||
              "Programme manager"
          )
          .join(", ");

  const facilitatorNames =
    facilitators === null
      ? "Not available"
      : facilitators.length ===
        0
      ? "Unassigned"
      : facilitators
          .map(
            (member) =>
              member.full_name ||
              member.email ||
              "Facilitator"
          )
          .join(", ");

  const facilitatorLabel =
    Array.isArray(
      facilitators
    ) &&
    facilitators.length > 1
      ? "Facilitators"
      : "Facilitator";

  const activeTeamMembers =
    teamMembers
      .filter(
        (member) =>
          member.status ===
            "active"
      )
      .sort(
        (a, b) =>
          String(
            a.full_name ||
              a.email ||
              ""
          ).localeCompare(
            String(
              b.full_name ||
                b.email ||
                ""
            )
          )
      );

  const selectedProgrammeManagerId =
    activeTeamMembers.find(
      (member) =>
        Boolean(
          cohortResponsibilitiesByMemberId[
            String(
              member.partner_member_id
            )
          ]?.isProgrammeManager
        )
    )?.partner_member_id ||
    "";

  const selectedFacilitatorId =
    activeTeamMembers.find(
      (member) =>
        Boolean(
          cohortResponsibilitiesByMemberId[
            String(
              member.partner_member_id
            )
          ]?.isFacilitator
        )
    )?.partner_member_id ||
    "";


  /* ------------------------------------------------------------------------ */
  /* Invitations                                                              */
  /* ------------------------------------------------------------------------ */

 const displayInvitations =
  Array.isArray(
    invitations
  )
    ? invitations
    : [];


/* ------------------------------------------------------------------------ */
/* Entrepreneur status rows                                                 */
/* ------------------------------------------------------------------------ */

const cohortAttentionRows =
  progressRows === null
    ? []
    : progressRows
        .map(
          (learner) => {
            const attention =
              getPartnerAttentionStatus(
                learner
              );

            if (!attention) {
              return null;
            }

            const facilitator =
              facilitatorAssignments.find(
                (assignment) =>
                  String(
                    assignment
                      .cohort_learner_id
                  ) ===
                  String(
                    learner
                      .cohort_learner_id
                  )
              );

            const facilitatorName =
              isFacilitator
                ? "You"
                : canViewFacilitatorTags
                ? facilitator
                    ?.facilitator_full_name ||
                  facilitator
                    ?.facilitator_email ||
                  "Unassigned"
                : "Not available";

            return {
              ...learner,

              attention_status:
                attention.key,

              attention_label:
                attention.label,

              attention_detail:
                attention.detail,

              attention_days:
                attention.days,

              facilitator_name:
                facilitatorName,
            };
          }
        )
        .filter(Boolean)
        .sort(
          (a, b) => {
            const priority = {
              stalled: 1,
              inactive: 2,
              watch: 3,
            };

            return (
              (
                priority[
                  a.attention_status
                ] || 99
              ) -
                (
                  priority[
                    b.attention_status
                  ] || 99
                ) ||
              (
                b.attention_days ||
                0
              ) -
                (
                  a.attention_days ||
                  0
                )
            );
          }
        );


/* ------------------------------------------------------------------------ */
/* Render                                                                   */
/* ------------------------------------------------------------------------ */

  return (
    <>

      {/* ==================================================================== */}
      {/* Back                                                                 */}
      {/* ==================================================================== */}

      <Link
        to="/partner/cohorts"
        className="inline-flex items-center gap-2 text-sm font-black text-[#FF914D] transition hover:text-[#E9782F]"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to cohorts
      </Link>


      {/* ==================================================================== */}
      {/* Messages                                                             */}
      {/* ==================================================================== */}

      {message && (
        <div
          role="status"
          className="mt-5 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-800"
        >
          {message}
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
        >
          {error}
        </div>
      )}


      {/* ==================================================================== */}
      {/* Header                                                               */}
      {/* ==================================================================== */}

      <header className="mt-6 overflow-hidden rounded-[28px] border border-[#E5E5E5] bg-white shadow-sm">

        <div className="p-6 md:p-8">

          <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">

            <div className="min-w-0">

              <p className="text-xs font-black uppercase tracking-[0.12em] text-[#FF914D]">
                {cohort.partner_name}
              </p>

              <h1 className="mt-2 break-words text-[34px] font-black tracking-[-0.03em] text-[#333333] md:text-[42px]">
                {cohort.name}
              </h1>

              <p className="mt-2 text-sm font-semibold text-[#757575] md:text-base">
                {programmeTypeLabel(
                  cohort.programme_type
                )}
              </p>

            </div>

            <span
              className={[
                "inline-flex w-fit rounded-full px-3 py-1 text-xs font-black capitalize",
                statusClasses(
                  cohortLifecycleStatus
                ),
              ].join(" ")}
            >
              {cohortLifecycleStatus}
            </span>

          </div>

        </div>

      </header>


      {/* ==================================================================== */}
      {/* Details                                                              */}
      {/* ==================================================================== */}

      <section className="mt-8">

        <article className="rounded-[26px] border border-[#DADADA] bg-white p-6 shadow-sm md:p-7">

          <p className="text-sm font-black uppercase tracking-[0.06em] text-[#FF914D]">
            Details
          </p>

          <div
            className={[
              "mt-6 grid gap-8",

              isFacilitator
                ? "md:grid-cols-2"
                : "md:grid-cols-2 xl:grid-cols-3",
            ].join(" ")}
          >

            <div className="space-y-6">

              <DetailField
                label="Duration"
                value={formatDuration(
                  cohort.start_date,
                  cohort.end_date
                )}
              />

              <DetailField
                label="Start date"
                value={formatDate(
                  cohort.start_date
                )}
              />

              <DetailField
                label="End date"
                value={formatDate(
                  cohort.end_date
                )}
              />

            </div>


            <div className="space-y-6">

              <DetailField
                label="Cohort size"
                value={
                  cohort.capacity
                    ? Number(
                        cohort.capacity
                      ).toLocaleString(
                        "en-ZA"
                      )
                    : "No limit"
                }
              />

              <DetailField
                label={
                  isFacilitator
                    ? "Active assigned learners"
                    : "Active learners"
                }
                value={metricValue(
                  activeLearners
                )}
              />

              {!isFacilitator && (
                <DetailField
                  label="Pending invitations"
                  value={metricValue(
                    pendingInvitations
                  )}
                />
              )}

            </div>


            {!isFacilitator && (
              <div className="space-y-6">

                <DetailField
                  label="Programme manager"
                  value={
                    programmeManagerNames
                  }
                />

                <DetailField
                  label="Facilitators"
                  value={
                    facilitatorNames
                  }
                />

              </div>
            )}

          </div>


          <div className="mt-8 border-t border-[#EAEAEA] pt-5">

            <p className="mb-3 text-[10px] font-black uppercase tracking-[0.12em] text-[#999999]">
              Cohort actions
            </p>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">

              <CohortNavButton
                onClick={
                  handleOpenLearnerProgress
                }
                label="Learner progress"
                icon={BarChart3}
              />

              <CohortNavLink
                to={`/partner/reports?cohort=${encodeURIComponent(
                  cohort.id
                )}&export=1`}
                label="Export Excel"
                icon={ExportExcelIcon}
              />

              {SHOW_LEARNERS_PAGE_NAV && (
                <CohortNavLink
                  to={`/partner/learners?cohort=${encodeURIComponent(
                    cohort.id
                  )}`}
                  label="Learners"
                  icon={Users}
                />
              )}

              {mayInvite && (
                <CohortNavButton
                  onClick={() =>
                    setInviting(
                      true
                    )
                  }
                  label="Invite entrepreneur"
                  icon={UserPlus}
                />
              )}

              {cohort.can_manage && (
                <CohortNavButton
                  onClick={() =>
                    setEditing(
                      true
                    )
                  }
                  label="Edit cohort"
                  icon={Edit3}
                />
              )}

              {cohort.can_manage &&
                cohort.status !==
                  "archived" && (
                  <CohortNavButton
                    onClick={
                      handleArchive
                    }
                    label={
                      archiving
                        ? "Archiving…"
                        : "Archive"
                    }
                    icon={Trash2}
                    disabled={
                      archiving
                    }
                    destructive
                  />
                )}

            </div>

          </div>

        </article>

      </section>


      {/* ==================================================================== */}
      {/* Visible Canva-style programme summary                                */}
      {/* ==================================================================== */}

      <section className="mt-4">

        {progressRows === null ? (
          <div className="rounded-[22px] border border-amber-200 bg-amber-50 p-5">

            <p className="font-black text-amber-900">
              Learner progress unavailable
            </p>

            <p className="mt-1 text-sm text-amber-800">
              {insightsError ||
                "Learner progress could not be loaded safely."}
            </p>

          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-3">

            <ProgrammeSummaryCard
              label="Average progress"
              value={percentageValue(
                programmeMetrics
                  ?.averageProgress ??
                  null
              )}
              icon={Users}
            />

            <ProgrammeSummaryCard
              label="Active entrepreneurs this week"
              value={
                programmeMetrics
                  ?.activeThisWeek ??
                0
              }
              icon={Activity}
            />

            <ProgrammeSummaryCard
              label="Entrepreneurs needing attention"
              value={
                needsAttentionCount ??
                0
              }
              icon={Clock3}
            />

          </div>
        )}

      </section>


      {/* ==================================================================== */}
      {/* Entrepreneur status tabs                                             */}
      {/* ==================================================================== */}

      <section className="mt-8">

        <EntrepreneurStatusTabs
          title={`${cohort.name} entrepreneurs`}
          description="Review entrepreneurs requiring follow-up, pending invitations and login inactivity within this cohort."
          attentionRows={
            cohortAttentionRows
          }
          invitationRows={
            displayInvitations
          }
          progressRows={
            progressRows || []
          }
          canViewPending={
            cohort.can_manage &&
            !isFacilitator
          }
          showCohort={false}
          loading={false}
          progressUnavailable={
            progressRows === null
          }
          pendingError={
            invitationsError
          }
        />

      </section>


      {/* ==================================================================== */}
      {/* Visible entrepreneur progress table                                  */}
      {/* ==================================================================== */}

      <section
        id="learner-progress"
        className="mt-8 scroll-mt-28"
      >

        <div className="mb-5">

          <h2 className="text-xl font-black uppercase tracking-[-0.02em] text-[#FF914D] md:text-2xl">
            {cohort.name} entrepreneurs
          </h2>

        </div>


        {progressRows === null ? (
          <div className="rounded-[22px] border border-amber-200 bg-amber-50 p-5">

            <p className="font-black text-amber-900">
              Entrepreneur progress unavailable
            </p>

            <p className="mt-1 text-sm text-amber-800">
              {insightsError ||
                "Entrepreneur progress could not be loaded safely."}
            </p>

          </div>
        ) : progressRows.length ===
          0 ? (
          <div className="rounded-[22px] border border-dashed border-[#DADADA] bg-white p-8 text-center">

            <p className="font-black text-[#333333]">
              No entrepreneurs yet
            </p>

            <p className="mt-2 text-sm text-[#757575]">
              Entrepreneurs will appear here
              once they have joined this cohort.
            </p>

          </div>
        ) : (
          <div className="overflow-hidden rounded-[22px] border border-[#D7D7D7] bg-white">

            <div className="overflow-x-auto">

              <table className="w-full min-w-[850px]">

                <thead className="bg-[#F6F6F6]">

                  <tr>

                    <th className="px-6 py-4 text-left text-xs font-black uppercase tracking-[0.04em] text-[#757575]">
                      Entrepreneur
                    </th>

                    <th className="px-6 py-4 text-left text-xs font-black uppercase tracking-[0.04em] text-[#757575]">
                      Facilitator
                    </th>

                    <th className="px-6 py-4 text-left text-xs font-black uppercase tracking-[0.04em] text-[#757575]">
                      Activity status
                    </th>

                    <th className="px-6 py-4 text-left text-xs font-black uppercase tracking-[0.04em] text-[#757575]">
                      Overall progress
                    </th>

                    <th
                      aria-label="Actions"
                      className="px-6 py-4"
                    />

                  </tr>

                </thead>


                <tbody className="divide-y divide-[#E5E5E5]">

                  {progressRows.map(
                    (learner) => {
                      const facilitator =
                        facilitatorAssignments.find(
                          (
                            assignment
                          ) =>
                            String(
                              assignment.cohort_learner_id
                            ) ===
                            String(
                              learner.cohort_learner_id
                            )
                        );

                      const facilitatorName =
                        isFacilitator
                          ? "You"
                          : canViewFacilitatorTags
                          ? facilitator
                              ?.facilitator_full_name ||
                            facilitator
                              ?.facilitator_email ||
                            "Unassigned"
                          : "Not available";

                      const activity =
                        learnerActivityStatus(
                          learner
                        );

                      const progress =
                        Math.max(
                          0,
                          Math.min(
                            100,
                            numberValue(
                              learner.overall_progress_percent
                            )
                          )
                        );

                      return (
                        <tr
                          key={
                            learner.cohort_learner_id
                          }
                          className="transition hover:bg-[#FFF9F5]"
                        >

                          <td className="px-6 py-5">

                            <p className="text-sm font-black text-[#333333]">
                              {learner.full_name ||
                                "Unnamed entrepreneur"}
                            </p>

                          </td>

                          <td className="px-6 py-5">

                            <p className="text-sm font-medium text-[#444444]">
                              {facilitatorName}
                            </p>

                          </td>

                          <td className="px-6 py-5">

                            <p className="text-sm font-black text-[#333333]">
                              {activity.label}
                            </p>

                            <p className="mt-1 text-xs text-[#757575]">
                              {activity.detail}
                            </p>

                          </td>

                          <td className="px-6 py-5">

                            <p className="text-sm font-black text-[#333333]">
                              {Math.round(
                                progress
                              )}
                              %
                            </p>

                          </td>

                          <td className="px-6 py-5 text-right">

                            <Link
                              to={`/partner/learners/${learner.cohort_learner_id}`}
                              className="inline-flex items-center justify-center rounded-full bg-black px-5 py-2.5 text-xs font-black text-white transition hover:bg-[#292929]"
                            >
                              View full profile
                            </Link>

                          </td>

                        </tr>
                      );
                    }
                  )}

                </tbody>

              </table>

            </div>

          </div>
        )}

      </section>


      {/* ==================================================================== */}
      {/* Hidden: detailed learner progress table                              */}
      {/* ==================================================================== */}

      {SHOW_DETAILED_LEARNER_PROGRESS_TABLE && (
        <section className="mt-8">

          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">

            <div>

              <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
                Learner progress
              </p>

              <h2 className="mt-1 text-2xl font-black tracking-[-0.02em] text-[#333333]">
                Detailed cohort progress
              </h2>

              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#757575]">
                {isFacilitator
                  ? "Review detailed learning progress for the entrepreneurs assigned to you."
                  : "Review each entrepreneur's detailed learning progress and open their full learner record."}
              </p>

            </div>


            {SHOW_LEARNERS_PAGE_NAV && (
              <Link
                to={`/partner/learners?cohort=${encodeURIComponent(
                  cohort.id
                )}`}
                className="inline-flex w-fit items-center rounded-full border border-[#FF914D] px-4 py-2.5 text-sm font-black text-[#FF914D] transition hover:bg-[#FFF4EC]"
              >
                <Users className="mr-2 h-4 w-4" />
                View learner list
              </Link>
            )}

          </div>


          {progressRows === null ? (
            <div className="mt-5 rounded-[24px] border border-amber-200 bg-amber-50 p-5">

              <p className="font-black text-amber-900">
                Learner progress unavailable
              </p>

              <p className="mt-1 text-sm text-amber-800">
                {insightsError ||
                  "The learner progress source could not be loaded safely."}
              </p>

            </div>
          ) : progressRows.length ===
            0 ? (
            <div className="mt-5 rounded-[24px] border border-dashed border-[#DADADA] bg-white p-8 text-center">

              <p className="font-black text-[#333333]">
                No learner progress yet
              </p>

              <p className="mt-2 text-sm text-[#757575]">
                Progress will appear here
                once entrepreneurs have joined
                the cohort and started learning.
              </p>

            </div>
          ) : (
            <div className="mt-5 overflow-hidden rounded-[24px] border border-[#E3E3E3] bg-white shadow-sm">

              <div className="overflow-x-auto">

                <table className="w-full min-w-[1180px]">

                  <thead className="bg-[#F7F7F7]">

                    <tr>

                      <TableHeading>
                        Entrepreneur
                      </TableHeading>

                      <TableHeading>
                        Progress
                      </TableHeading>

                      <TableHeading>
                        Current position
                      </TableHeading>

                      <TableHeading>
                        Levels
                      </TableHeading>

                      <TableHeading>
                        Tasks
                      </TableHeading>

                      <TableHeading>
                        XP
                      </TableHeading>

                      <TableHeading>
                        Last activity
                      </TableHeading>

                      <TableHeading align="right">
                        Details
                      </TableHeading>

                    </tr>

                  </thead>


                  <tbody className="divide-y divide-[#EEEEEE]">

                    {progressRows.map(
                      (learner) => {
                        const progress =
                          Math.max(
                            0,
                            Math.min(
                              100,
                              Number(
                                learner.overall_progress_percent ||
                                  0
                              )
                            )
                          );

                        return (
                          <tr
                            key={
                              learner.cohort_learner_id
                            }
                            className="transition hover:bg-[#FFF9F5]"
                          >

                            <td className="px-5 py-4">

                              <p className="font-black text-[#333333]">
                                {learner.full_name ||
                                  "Unnamed entrepreneur"}
                              </p>

                              <p className="mt-1 text-xs text-[#757575]">
                                {learner.email}
                              </p>

                            </td>


                            <td className="px-5 py-4">

                              <div className="min-w-[130px]">

                                <span className="text-sm font-black text-[#333333]">
                                  {Math.round(
                                    progress
                                  )}
                                  %
                                </span>

                                <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#EEEEEE]">

                                  <div
                                    className="h-full rounded-full bg-[#FF914D]"
                                    style={{
                                      width: `${progress}%`,
                                    }}
                                  />

                                </div>

                              </div>

                            </td>


                            <td className="max-w-[220px] px-5 py-4">

                              <p className="text-sm font-bold leading-relaxed text-[#444444]">
                                {progressPositionLabel(
                                  learner
                                )}
                              </p>

                            </td>


                            <td className="px-5 py-4 text-sm font-bold text-[#444444]">

                              {numberValue(
                                learner.levels_completed
                              )}

                              {" / "}

                              {numberValue(
                                learner.total_levels
                              )}

                            </td>


                            <td className="px-5 py-4 text-sm font-bold text-[#444444]">

                              {numberValue(
                                learner.tasks_completed
                              )}

                              {" / "}

                              {numberValue(
                                learner.tasks_total
                              )}

                            </td>


                            <td className="px-5 py-4 text-sm font-black text-[#333333]">

                              {numberValue(
                                learner.total_xp
                              ).toLocaleString(
                                "en-ZA"
                              )}

                            </td>


                            <td className="px-5 py-4">

                              <p className="text-sm font-semibold text-[#555555]">
                                {formatActivityAge(
                                  learner.days_since_activity
                                )}
                              </p>

                              {learner.last_activity_at && (
                                <p className="mt-1 text-xs text-[#999999]">
                                  {formatTimestamp(
                                    learner.last_activity_at
                                  )}
                                </p>
                              )}

                            </td>


                            <td className="px-5 py-4 text-right">

                              <Link
                                to={`/partner/learners/${learner.cohort_learner_id}`}
                                className="inline-flex items-center rounded-full bg-black px-4 py-2 text-xs font-black text-white transition hover:bg-[#292929]"
                              >
                                View
                              </Link>

                            </td>

                          </tr>
                        );
                      }
                    )}

                  </tbody>

                </table>

              </div>

            </div>
          )}

        </section>
      )}


            {/* ==================================================================== */}
      {/* Invitations                                                          */}
      {/* ==================================================================== */}

      {cohort.can_manage &&
        !isFacilitator && (
          <section className="mt-8">

            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">

              <div>

                <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
                  Invitations
                </p>


                <h2 className="mt-1 text-2xl font-black tracking-[-0.02em] text-[#333333]">
                  Entrepreneur invitations
                </h2>


                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#757575]">
                  Manage entrepreneur
                  invitations for this cohort
                  directly from the cohort
                  page.
                </p>

              </div>


              {mayInvite && (
                <button
                  type="button"
                  onClick={() =>
                    setInviting(
                      true
                    )
                  }
                  className="inline-flex w-fit items-center rounded-full bg-[#FF914D] px-5 py-3 text-sm font-black text-white transition hover:bg-[#E9782F]"
                >
                  <UserPlus className="mr-2 h-4 w-4" />

                  Invite entrepreneur
                </button>
              )}

            </div>


            {invitationsError && (
              <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
                {invitationsError}
              </div>
            )}


            {displayInvitations.length ===
            0 ? (
              <div className="mt-5 rounded-[24px] border border-dashed border-[#DADADA] bg-white p-7 text-center">

                <Mail className="mx-auto h-6 w-6 text-[#FF914D]" />


                <p className="mt-3 font-black text-[#333333]">
                  No invitations yet
                </p>


                <p className="mt-2 text-sm text-[#757575]">
                  Invitations sent to
                  entrepreneurs will appear
                  here.
                </p>

              </div>
            ) : (
              <div className="mt-5 overflow-hidden rounded-[24px] border border-[#E7E7E7] bg-white shadow-sm">

                {/* ========================================================== */}
                {/* Scrollable invitation history                              */}
                {/* ========================================================== */}

                <div className="max-h-[420px] overflow-auto">

                  {/* ======================================================== */}
                  {/* Desktop / tablet table                                   */}
                  {/* ======================================================== */}

                  <div className="hidden min-w-[900px] md:block">

                    <table className="w-full border-collapse">

                      <thead className="sticky top-0 z-20 bg-[#FAFAFA] shadow-[0_1px_0_0_#E7E7E7]">

                        <tr>

                          <th className="w-[22%] px-5 py-4 text-left text-[11px] font-black uppercase tracking-wider text-[#777777]">
                            Name
                          </th>


                          <th className="w-[27%] px-5 py-4 text-left text-[11px] font-black uppercase tracking-wider text-[#777777]">
                            Email
                          </th>


                          <th className="w-[13%] px-5 py-4 text-left text-[11px] font-black uppercase tracking-wider text-[#777777]">
                            Status
                          </th>


                          <th className="w-[18%] px-5 py-4 text-left text-[11px] font-black uppercase tracking-wider text-[#777777]">
                            Expiry
                          </th>


                          <th className="w-[20%] px-5 py-4 text-right text-[11px] font-black uppercase tracking-wider text-[#777777]">
                            Actions
                          </th>

                        </tr>

                      </thead>


                      <tbody className="divide-y divide-[#EEEEEE]">

                        {displayInvitations.map(
                          (invitation) => {
                            const isChanging =
                              changingInvitationId ===
                              invitation.invitation_id;


                            const isCopying =
                              copyingInvitationId ===
                              invitation.invitation_id;


                            const isCopied =
                              copiedInvitationId ===
                              invitation.invitation_id;


                            const displayName =
                              invitation
                                .invitee_name ||
                              "Entrepreneur";


                            const canManagePendingInvite =
                              invitation.can_manage &&
                              invitation.status ===
                                "pending";


                            return (
                              <tr
                                key={
                                  invitation.invitation_id
                                }
                                className="bg-white transition hover:bg-[#FFFDFC]"
                              >

                                {/* Name */}

                                <td className="px-5 py-4 align-middle">

                                  <p className="font-black text-[#333333]">
                                    {displayName}
                                  </p>


                                  {invitation.invited_at && (
                                    <p className="mt-1 text-[11px] text-[#999999]">
                                      Invited{" "}
                                      {formatTimestamp(
                                        invitation.invited_at
                                      )}
                                    </p>
                                  )}

                                </td>


                                {/* Email */}

                                <td className="px-5 py-4 align-middle">

                                  <p className="break-all text-sm font-semibold text-[#666666]">
                                    {
                                      invitation.email
                                    }
                                  </p>

                                </td>


                                {/* Status */}

                                <td className="px-5 py-4 align-middle">

                                  <span
                                    className={[
                                      "inline-flex rounded-full px-2.5 py-1 text-[10px] font-black capitalize",

                                      invitationStatusClasses(
                                        invitation.status
                                      ),
                                    ].join(" ")}
                                  >
                                    {
                                      invitation.status
                                    }
                                  </span>

                                </td>


                                {/* Expiry */}

                                <td className="px-5 py-4 align-middle">

                                  {invitation.expires_at ? (
                                    <p className="text-xs font-semibold text-[#757575]">
                                      {formatTimestamp(
                                        invitation.expires_at
                                      )}
                                    </p>
                                  ) : (
                                    <span className="text-xs text-[#BBBBBB]">
                                      —
                                    </span>
                                  )}

                                </td>


                                {/* Actions */}

                                <td className="px-5 py-4 align-middle">

                                  <div className="flex items-center justify-end gap-2">

                                    {canManagePendingInvite ? (
                                      <>

                                        <button
                                          type="button"
                                          disabled={
                                            isCopying ||
                                            isChanging
                                          }
                                          onClick={() =>
                                            handleCopyInvitationLink(
                                              invitation
                                            )
                                          }
                                          className="inline-flex whitespace-nowrap items-center rounded-full border border-[#FFD7BE] bg-[#FFF7F1] px-3.5 py-2.5 text-xs font-black text-[#D95F1E] transition hover:bg-[#FFF0E6] disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                          <Copy className="mr-2 h-4 w-4 flex-none" />

                                          {isCopying
                                            ? "Copying…"
                                            : isCopied
                                            ? "Link copied"
                                            : "Copy link"}
                                        </button>


                                        <button
                                          type="button"
                                          disabled={
                                            isChanging ||
                                            isCopying
                                          }
                                          onClick={() =>
                                            handleRevokeInvitation(
                                              invitation
                                            )
                                          }
                                          className="inline-flex whitespace-nowrap items-center rounded-full border border-red-200 bg-red-50 px-3.5 py-2.5 text-xs font-black text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                          <Trash2 className="mr-2 h-4 w-4 flex-none" />

                                          {isChanging
                                            ? "Revoking…"
                                            : "Revoke"}
                                        </button>

                                      </>
                                    ) : (
                                      <span className="text-xs font-semibold text-[#BBBBBB]">
                                        —
                                      </span>
                                    )}

                                  </div>

                                </td>

                              </tr>
                            );
                          }
                        )}

                      </tbody>

                    </table>

                  </div>


                  {/* ======================================================== */}
                  {/* Mobile invitation cards                                  */}
                  {/* ======================================================== */}

                  <div className="divide-y divide-[#EEEEEE] md:hidden">

                    {displayInvitations.map(
                      (invitation) => {
                        const isChanging =
                          changingInvitationId ===
                          invitation.invitation_id;


                        const isCopying =
                          copyingInvitationId ===
                          invitation.invitation_id;


                        const isCopied =
                          copiedInvitationId ===
                          invitation.invitation_id;


                        const displayName =
                          invitation
                            .invitee_name ||
                          "Entrepreneur";


                        const canManagePendingInvite =
                          invitation.can_manage &&
                          invitation.status ===
                            "pending";


                        return (
                          <article
                            key={
                              invitation.invitation_id
                            }
                            className="bg-white p-5"
                          >

                            <div className="flex items-start justify-between gap-3">

                              <div className="min-w-0">

                                <p className="font-black text-[#333333]">
                                  {displayName}
                                </p>


                                <p className="mt-1 break-all text-sm text-[#757575]">
                                  {
                                    invitation.email
                                  }
                                </p>

                              </div>


                              <span
                                className={[
                                  "inline-flex flex-none rounded-full px-2.5 py-1 text-[10px] font-black capitalize",

                                  invitationStatusClasses(
                                    invitation.status
                                  ),
                                ].join(" ")}
                              >
                                {
                                  invitation.status
                                }
                              </span>

                            </div>


                            <div className="mt-4 grid gap-3 rounded-2xl bg-[#FAFAFA] p-3 text-xs sm:grid-cols-2">

                              <div>

                                <p className="font-black uppercase tracking-wider text-[#AAAAAA]">
                                  Invited
                                </p>


                                <p className="mt-1 font-semibold text-[#666666]">
                                  {invitation.invited_at
                                    ? formatTimestamp(
                                        invitation.invited_at
                                      )
                                    : "—"}
                                </p>

                              </div>


                              <div>

                                <p className="font-black uppercase tracking-wider text-[#AAAAAA]">
                                  Expires
                                </p>


                                <p className="mt-1 font-semibold text-[#666666]">
                                  {invitation.expires_at
                                    ? formatTimestamp(
                                        invitation.expires_at
                                      )
                                    : "—"}
                                </p>

                              </div>

                            </div>


                            {canManagePendingInvite && (
                              <div className="mt-4 flex flex-col gap-2 sm:flex-row">

                                <button
                                  type="button"
                                  disabled={
                                    isCopying ||
                                    isChanging
                                  }
                                  onClick={() =>
                                    handleCopyInvitationLink(
                                      invitation
                                    )
                                  }
                                  className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-full border border-[#FFD7BE] bg-[#FFF7F1] px-4 py-2.5 text-xs font-black text-[#D95F1E] transition hover:bg-[#FFF0E6] disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  <Copy className="mr-2 h-4 w-4" />

                                  {isCopying
                                    ? "Copying…"
                                    : isCopied
                                    ? "Link copied"
                                    : "Copy invitation link"}
                                </button>


                                <button
                                  type="button"
                                  disabled={
                                    isChanging ||
                                    isCopying
                                  }
                                  onClick={() =>
                                    handleRevokeInvitation(
                                      invitation
                                    )
                                  }
                                  className="inline-flex min-h-[44px] items-center justify-center rounded-full border border-red-200 bg-red-50 px-4 py-2.5 text-xs font-black text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />

                                  {isChanging
                                    ? "Revoking…"
                                    : "Revoke"}
                                </button>

                              </div>
                            )}

                          </article>
                        );
                      }
                    )}

                  </div>

                </div>


                {/* ========================================================== */}
                {/* Invitation table footer                                    */}
                {/* ========================================================== */}

                <div className="flex items-center justify-between border-t border-[#EEEEEE] bg-[#FAFAFA] px-5 py-3">

                  <p className="text-xs font-semibold text-[#999999]">
                    {displayInvitations.length}{" "}
                    {displayInvitations.length ===
                    1
                      ? "invitation"
                      : "invitations"}
                  </p>


                  {displayInvitations.length >
                    4 && (
                    <p className="text-xs font-semibold text-[#999999]">
                      Scroll to view more
                    </p>
                  )}

                </div>

              </div>
            )}

          </section>
        )}


      {/* ==================================================================== */}
      {/* Staff                                                                */}
      {/* ==================================================================== */}

      {cohort.can_manage &&
        !isFacilitator && (
          <section className="mt-8">

            <div>

              <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
                Staff
              </p>

              <h2 className="mt-1 text-2xl font-black tracking-[-0.02em] text-[#333333]">
                Programme team
              </h2>

              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#757575]">
                Select the Programme Manager and Facilitator for this cohort from your active Partner team. The same team member can fill both roles.
              </p>

            </div>


            {facilitatorError && (
              <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">
                {facilitatorError}
              </div>
            )}


            <div className="mt-5 grid gap-4 md:grid-cols-2">

              <article className="rounded-[22px] border border-[#E7E7E7] bg-white p-5 shadow-sm">

                <div className="flex items-start gap-3">

                  <div className="flex h-10 w-10 flex-none items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
                    <UserCog className="h-5 w-5" />
                  </div>

                  <div className="min-w-0 flex-1">

                    <p className="text-xs font-black uppercase tracking-wider text-[#999999]">
                      Programme Manager
                    </p>

                    {canManageTeam ? (
                      <select
                        value={
                          selectedProgrammeManagerId
                        }
                        onChange={(event) =>
                          handleStaffRoleChange(
                            "isProgrammeManager",
                            event.target.value
                          )
                        }
                        disabled={
                          savingStaffRole !==
                          ""
                        }
                        className="mt-3 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm font-semibold text-[#333333] outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <option value="">
                          Unassigned
                        </option>

                        {activeTeamMembers.map(
                          (member) => (
                            <option
                              key={
                                member.partner_member_id
                              }
                              value={
                                member.partner_member_id
                              }
                            >
                              {member.full_name ||
                                member.email ||
                                "Team member"}
                            </option>
                          )
                        )}
                      </select>
                    ) : (
                      <p className="mt-2 font-black leading-relaxed text-[#333333]">
                        {programmeManagerNames}
                      </p>
                    )}

                  </div>

                </div>

              </article>


              <article className="rounded-[22px] border border-[#E7E7E7] bg-white p-5 shadow-sm">

                <div className="flex items-start gap-3">

                  <div className="flex h-10 w-10 flex-none items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
                    <Users className="h-5 w-5" />
                  </div>

                  <div className="min-w-0 flex-1">

                    <p className="text-xs font-black uppercase tracking-wider text-[#999999]">
                      Facilitator
                    </p>

                    {canManageTeam ? (
                      <select
                        value={
                          selectedFacilitatorId
                        }
                        onChange={(event) =>
                          handleStaffRoleChange(
                            "isFacilitator",
                            event.target.value
                          )
                        }
                        disabled={
                          savingStaffRole !==
                          ""
                        }
                        className="mt-3 w-full rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm font-semibold text-[#333333] outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <option value="">
                          Unassigned
                        </option>

                        {activeTeamMembers.map(
                          (member) => (
                            <option
                              key={
                                member.partner_member_id
                              }
                              value={
                                member.partner_member_id
                              }
                            >
                              {member.full_name ||
                                member.email ||
                                "Team member"}
                            </option>
                          )
                        )}
                      </select>
                    ) : (
                      <p className="mt-2 font-black leading-relaxed text-[#333333]">
                        {facilitatorNames}
                      </p>
                    )}

                  </div>

                </div>

              </article>

            </div>

          </section>
        )}


      {/* ==================================================================== */}
      {/* Hidden: Programme overview / indicators / trends                     */}
      {/* ==================================================================== */}

      {SHOW_ADVANCED_PROGRAMME_SECTIONS && (
        <>

          <section className="mt-10">

            <div>

              <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
                Programme
              </p>

              <h2 className="mt-1 text-2xl font-black tracking-[-0.02em] text-[#333333]">
                Programme overview
              </h2>

              <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#757575]">
                A concise view of this
                cohort&apos;s learners,
                schedule, facilitation and
                programme setup.
              </p>

            </div>


            <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">

              <OverviewCard
                label={
                  isFacilitator
                    ? "Assigned learners"
                    : "Learners"
                }
                value={metricValue(
                  learnerPipeline
                )}
                description={
                  isFacilitator
                    ? "Learners available to your facilitator access"
                    : "Registered learners + pending learner invitations"
                }
                icon={Users}
              />

              <OverviewCard
                label="Runs"
                value={formatDateRange(
                  cohort.start_date,
                  cohort.end_date
                )}
                description="Programme run dates"
                icon={CalendarDays}
                textValue
              />

              <OverviewCard
                label={
                  facilitatorLabel
                }
                value={
                  facilitatorNames
                }
                description={
                  facilitatorError ||
                  "Assigned programme facilitator staff"
                }
                icon={UserCog}
                textValue
              />

              <OverviewCard
                label="Registered learners"
                value={metricValue(
                  registeredLearners
                )}
                description="Learner-only cohort memberships"
                icon={Users}
              />

              <OverviewCard
                label="Activated learners"
                value={metricValue(
                  programmeMetrics
                    ?.activated ??
                    null
                )}
                description="Reached a first learning or practical result"
                icon={Award}
              />


              <article className="rounded-[22px] border border-[#EFEFEF] bg-white p-5 shadow-sm transition hover:border-[#FFD2B5]">

                <div className="flex items-start justify-between gap-4">

                  <div className="min-w-0 flex-1">

                    <p className="text-xs font-black uppercase tracking-wider text-[#757575]">
                      Format
                    </p>

                    {!editingFormat ? (
                      <>

                        <p className="mt-3 whitespace-pre-line text-base font-black leading-relaxed text-[#333333]">
                          {formatError
                            ? "Not available"
                            : programmeFormat ||
                              "Not set"}
                        </p>

                        <p className="mt-2 text-xs leading-relaxed text-[#757575]">
                          How this programme is
                          delivered.
                        </p>

                        {cohort.can_manage && (
                          <button
                            type="button"
                            onClick={() => {
                              setFormatDraft(
                                programmeFormat
                              );

                              setEditingFormat(
                                true
                              );
                            }}
                            className="mt-4 inline-flex items-center text-sm font-black text-[#FF914D] transition hover:text-[#E9782F]"
                          >
                            <Edit3 className="mr-2 h-4 w-4" />
                            Edit format
                          </button>
                        )}

                      </>
                    ) : (
                      <div className="mt-3">

                        <textarea
                          value={
                            formatDraft
                          }
                          onChange={(
                            event
                          ) =>
                            setFormatDraft(
                              event.target
                                .value
                            )
                          }
                          maxLength={
                            500
                          }
                          rows={5}
                          placeholder="Example: Fortnightly in-person sessions with weekly self-paced learning."
                          className="w-full resize-y rounded-2xl border border-[#DDDDDD] bg-white px-4 py-3 text-sm text-[#333333] outline-none transition focus:border-[#FF914D] focus:ring-2 focus:ring-[#FFE1CF]"
                        />

                        <div className="mt-2 text-xs text-[#757575]">
                          {formatDraft.length}
                          /500
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2">

                          <button
                            type="button"
                            onClick={
                              handleSaveFormat
                            }
                            disabled={
                              savingFormat
                            }
                            className="rounded-full bg-[#FF914D] px-4 py-2 text-sm font-black text-white transition hover:bg-[#E9782F] disabled:opacity-60"
                          >
                            {savingFormat
                              ? "Saving…"
                              : "Save"}
                          </button>

                          <button
                            type="button"
                            disabled={
                              savingFormat
                            }
                            onClick={() => {
                              setFormatDraft(
                                programmeFormat
                              );

                              setEditingFormat(
                                false
                              );

                              setFormatError(
                                ""
                              );
                            }}
                            className="rounded-full border border-[#DDDDDD] px-4 py-2 text-sm font-black text-[#333333] transition hover:border-[#FF914D]"
                          >
                            Cancel
                          </button>

                        </div>

                      </div>
                    )}

                    {formatError && (
                      <p className="mt-3 text-xs font-semibold text-amber-700">
                        {formatError}
                      </p>
                    )}

                  </div>

                  <div className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
                    <BookOpen className="h-5 w-5" />
                  </div>

                </div>

              </article>

            </div>

          </section>


          <section className="mt-10">

            <div>

              <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
                Performance
              </p>

              <h2 className="mt-1 text-2xl font-black tracking-[-0.02em] text-[#333333]">
                Programme indicators
              </h2>

            </div>


            {(countsError ||
              insightsError) && (
              <div className="mt-5 rounded-[22px] border border-amber-200 bg-amber-50 p-4">

                {countsError && (
                  <p className="text-sm text-amber-800">
                    {countsError}
                  </p>
                )}

                {insightsError && (
                  <p className="mt-1 text-sm text-amber-800">
                    {insightsError}
                  </p>
                )}

              </div>
            )}


            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">

              <IndicatorCard
                label={
                  isFacilitator
                    ? "Active assigned"
                    : "Active learners"
                }
                value={metricValue(
                  activeLearners
                )}
                description="Active cohort memberships"
                icon={Activity}
              />

              <IndicatorCard
                label="Active this week"
                value={metricValue(
                  programmeMetrics
                    ?.activeThisWeek ??
                    null
                )}
                description="Activity recorded in the last 7 days"
                icon={Clock3}
              />

              <IndicatorCard
                label="Average progress"
                value={percentageValue(
                  programmeMetrics
                    ?.averageProgress ??
                    null
                )}
                description="Average overall learner progress"
                icon={BarChart3}
              />

              <IndicatorCard
                label="Levels completed"
                value={metricValue(
                  programmeMetrics
                    ?.levelsCompleted ??
                    null
                )}
                description="Total completed learning levels"
                icon={Award}
              />

              <IndicatorCard
                label="Tasks completed"
                value={metricValue(
                  programmeMetrics
                    ?.tasksCompleted ??
                    null
                )}
                description="Completed practical tasks"
                icon={ListChecks}
              />

              <IndicatorCard
                label="Outstanding tasks"
                value={metricValue(
                  programmeMetrics
                    ?.tasksOutstanding ??
                    null
                )}
                description="Unlocked practical work still outstanding"
                icon={ListChecks}
              />

              <IndicatorCard
                label={
                  isFacilitator
                    ? "Completed assigned"
                    : "Completed learners"
                }
                value={metricValue(
                  completedLearners
                )}
                description="Completed cohort memberships"
                icon={Award}
              />

              {!isFacilitator && (
                <IndicatorCard
                  label="Pending invitations"
                  value={metricValue(
                    pendingInvitations
                  )}
                  description="Entrepreneur invitations awaiting acceptance"
                  icon={UserPlus}
                />
              )}

            </div>

          </section>


          <section className="mt-10">

            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">

              <div>

                <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
                  Trends
                </p>

                <h2 className="mt-1 text-2xl font-black tracking-[-0.02em] text-[#333333]">
                  Cohort trends
                </h2>

              </div>


              <div className="inline-flex rounded-full border border-[#EFEFEF] bg-white p-1 shadow-sm">

                {[6, 8, 12].map(
                  (weeks) => (
                    <button
                      key={
                        weeks
                      }
                      type="button"
                      onClick={() =>
                        setTrendWeeks(
                          weeks
                        )
                      }
                      className={[
                        "rounded-full px-4 py-2 text-xs font-black transition",

                        trendWeeks ===
                        weeks
                          ? "bg-[#FF914D] text-white"
                          : "text-[#757575] hover:bg-[#FFF4EC] hover:text-[#FF914D]",
                      ].join(" ")}
                    >
                      {weeks} weeks
                    </button>
                  )
                )}

              </div>

            </div>


            {progressRows === null ? (
              <div className="mt-5 rounded-[24px] border border-amber-200 bg-amber-50 p-5">

                <p className="font-black text-amber-900">
                  Cohort trends unavailable
                </p>

                <p className="mt-1 text-sm text-amber-800">
                  {insightsError}
                </p>

              </div>
            ) : (
              <div className="mt-5 grid gap-5 xl:grid-cols-3">

                <TrendChart
                  title="Latest learner activity"
                  description="When each learner's latest programme activity occurred."
                  data={
                    activityTrend
                  }
                  icon={Activity}
                />

                <TrendChart
                  title="Latest level completions"
                  description="When each learner's latest completed level occurred."
                  data={
                    levelTrend
                  }
                  icon={Award}
                />

                <TrendChart
                  title="Latest practical completions"
                  description="When each learner's latest practical task completion occurred."
                  data={
                    taskTrend
                  }
                  icon={ListChecks}
                />

              </div>
            )}

          </section>

        </>
      )}


      {/* ==================================================================== */}
      {/* Hidden: Cohort curriculum                                            */}
      {/* ==================================================================== */}

      {SHOW_COHORT_CURRICULUM && (
        <PartnerCohortCurriculumPanel
          cohortId={
            cohort.id
          }
          canManage={
            cohort.can_manage ===
            true
          }
        />
      )}


      {/* ==================================================================== */}
      {/* Modals                                                               */}
      {/* ==================================================================== */}

      <EditCohortModal
        open={
          editing
        }
        cohort={
          cohort
        }
        teamMembers={
          teamMembers
        }
        canManageTeam={
          canManageTeam
        }
        onClose={() =>
          setEditing(
            false
          )
        }
        onUpdated={
          loadCohort
        }
      />


      <InviteLearnerModal
        open={
          inviting
        }
        cohorts={[
          {
            id:
              cohort.id,

            name:
              cohort.name,

            status:
              cohort.status,

            can_manage:
              cohort.can_manage,
          },
        ]}
        initialCohortId={
          cohort.id
        }
        lockCohort
        onClose={() =>
          setInviting(
            false
          )
        }
        onInvited={
          handleInvited
        }
      />

    </>
  );
}


/* -------------------------------------------------------------------------- */
/* Details                                                                    */
/* -------------------------------------------------------------------------- */

function DetailField({
  label,
  value,
}) {
  return (
    <div>

      <p className="text-[11px] font-black uppercase tracking-[0.08em] text-[#999999]">
        {label}
      </p>

      <p className="mt-1.5 whitespace-pre-line text-base font-black leading-relaxed text-[#333333]">
        {value}
      </p>

    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* Hidden detailed progress table heading                                     */
/* -------------------------------------------------------------------------- */

function TableHeading({
  children,
  align = "left",
}) {
  return (
    <th
      scope="col"
      className={[
        "whitespace-nowrap px-5 py-3 text-xs font-black uppercase tracking-wider text-[#757575]",

        align ===
        "right"
          ? "text-right"
          : "text-left",
      ].join(" ")}
    >
      {children}
    </th>
  );
}


/* -------------------------------------------------------------------------- */
/* Canva-style summary card                                                   */
/* -------------------------------------------------------------------------- */

function ProgrammeSummaryCard({
  label,
  value,
  icon: Icon,
}) {
  return (
    <article className="min-h-[160px] rounded-[22px] border border-[#DADADA] bg-white p-6 shadow-sm">

      <div className="flex items-center gap-3">

        <div className="flex h-8 w-8 flex-none items-center justify-center text-[#FF914D]">
          <Icon className="h-5 w-5" />
        </div>

        <p className="text-sm font-semibold text-[#757575]">
          {label}
        </p>

      </div>

      <p className="mt-8 text-4xl font-black tracking-[-0.04em] text-black">
        {value}
      </p>

    </article>
  );
}


/* -------------------------------------------------------------------------- */
/* Hidden overview card                                                       */
/* -------------------------------------------------------------------------- */

function OverviewCard({
  label,
  value,
  description,
  icon: Icon,
  textValue = false,
}) {
  return (
    <article className="rounded-[22px] border border-[#EFEFEF] bg-white p-5 shadow-sm transition hover:border-[#FFD2B5]">

      <div className="flex items-start justify-between gap-4">

        <div className="min-w-0">

          <p className="text-xs font-black uppercase tracking-wider text-[#757575]">
            {label}
          </p>

          <p
            className={[
              "mt-3 font-black text-[#333333]",

              textValue
                ? "text-base leading-relaxed"
                : "text-3xl tracking-[-0.03em]",
            ].join(" ")}
          >
            {value}
          </p>

          <p className="mt-2 text-xs leading-relaxed text-[#757575]">
            {description}
          </p>

        </div>

        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
          <Icon className="h-5 w-5" />
        </div>

      </div>

    </article>
  );
}


/* -------------------------------------------------------------------------- */
/* Hidden indicator card                                                      */
/* -------------------------------------------------------------------------- */

function IndicatorCard({
  label,
  value,
  description,
  icon: Icon,
}) {
  return (
    <article className="rounded-[22px] border border-[#EFEFEF] bg-white p-5 shadow-sm transition hover:border-[#FFD2B5]">

      <div className="flex items-start justify-between gap-4">

        <div>

          <p className="text-xs font-black uppercase tracking-wider text-[#757575]">
            {label}
          </p>

          <p className="mt-3 text-3xl font-black tracking-[-0.03em] text-[#333333]">
            {value}
          </p>

          <p className="mt-2 text-xs leading-relaxed text-[#757575]">
            {description}
          </p>

        </div>

        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
          <Icon className="h-5 w-5" />
        </div>

      </div>

    </article>
  );
}


/* -------------------------------------------------------------------------- */
/* Hidden trend chart                                                         */
/* -------------------------------------------------------------------------- */

function TrendChart({
  title,
  description,
  data,
  icon: Icon,
}) {
  const total =
    data.reduce(
      (
        sum,
        bucket
      ) =>
        sum +
        bucket.count,
      0
    );

  const maximum =
    Math.max(
      0,
      ...data.map(
        (bucket) =>
          bucket.count
      )
    );

  return (
    <article className="rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm">

      <div className="flex items-start gap-3">

        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
          <Icon className="h-5 w-5" />
        </div>

        <div>

          <h3 className="font-black text-[#333333]">
            {title}
          </h3>

          <p className="mt-1 text-xs leading-relaxed text-[#757575]">
            {description}
          </p>

        </div>

      </div>


      {total === 0 ? (
        <div className="mt-6 flex min-h-[170px] items-center justify-center rounded-2xl bg-[#FFF9F5] px-5 text-center">

          <p className="text-sm font-semibold text-[#757575]">
            No events recorded in this
            period.
          </p>

        </div>
      ) : (
        <div className="mt-6 overflow-x-auto">

          <div
            className="flex h-[190px] items-end gap-3"
            style={{
              minWidth: `${Math.max(
                460,
                data.length *
                  58
              )}px`,
            }}
          >

            {data.map(
              (bucket) => {
                const height =
                  maximum > 0
                    ? Math.max(
                        10,
                        (
                          bucket.count /
                          maximum
                        ) *
                          120
                      )
                    : 10;

                return (
                  <div
                    key={
                      bucket.key
                    }
                    className="flex min-w-0 flex-1 flex-col items-center"
                  >

                    <span className="mb-2 text-xs font-black text-[#333333]">
                      {
                        bucket.count
                      }
                    </span>

                    <div className="flex h-[120px] w-full items-end justify-center">

                      <div
                        className="w-full max-w-[34px] rounded-t-lg bg-[#FF914D] transition-all"
                        style={{
                          height: `${height}px`,
                        }}
                      />

                    </div>

                    <span className="mt-2 whitespace-nowrap text-[10px] font-bold text-[#757575]">
                      {
                        bucket.label
                      }
                    </span>

                  </div>
                );
              }
            )}

          </div>

        </div>
      )}

    </article>
  );
}


function ExportExcelIcon({
  className = "",
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M12 3v12" />
      <path d="m7 10 5 5 5-5" />
      <path d="M5 21h14" />
    </svg>
  );
}


/* -------------------------------------------------------------------------- */
/* Cohort action link                                                         */
/* -------------------------------------------------------------------------- */

function CohortNavLink({
  to,
  label,
  icon: Icon,
}) {
  return (
    <Link
      to={
        to
      }
      className="group inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border border-[#E9E9E9] bg-white px-4 py-2.5 text-sm font-black text-[#444444] transition hover:border-[#FF914D] hover:bg-[#FFF4EC] hover:text-[#E9782F]"
    >
      {Icon ? (
        <Icon className="h-4 w-4 flex-none text-[#FF914D]" />
      ) : null}

      <span>
        {label}
      </span>
    </Link>
  );
}


/* -------------------------------------------------------------------------- */
/* Cohort action button                                                       */
/* -------------------------------------------------------------------------- */

function CohortNavButton({
  onClick,
  label,
  icon: Icon,
  disabled = false,
  destructive = false,
}) {
  return (
    <button
      type="button"
      onClick={
        onClick
      }
      disabled={
        disabled
      }
      className={[
        "inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border px-4 py-2.5 text-sm font-black transition disabled:cursor-not-allowed disabled:opacity-50",

        destructive
          ? "border-red-200 bg-red-50 text-red-700 hover:border-red-300 hover:bg-red-100"
          : "border-[#E9E9E9] bg-white text-[#444444] hover:border-[#FF914D] hover:bg-[#FFF4EC] hover:text-[#E9782F]",
      ].join(" ")}
    >

      {Icon ? (
        <Icon
          className={[
            "h-4 w-4 flex-none",

            destructive
              ? "text-red-600"
              : "text-[#FF914D]",
          ].join(" ")}
        />
      ) : null}

      <span>
        {label}
      </span>

    </button>
  );
}